import { create } from "zustand";

// Define your store's state
type Store = {
  signal: {
    name: string;
    description: string;
    trigger: "usage_based" | "event_based";
    entity_relation: "users" | "accounts";
    automation_type: "signal" | "automation";
    is_precreated: boolean;
    filters: {
      query_type: "OR";
      predicates: {
        query_type: "AND";
        predicates: {
          entity_type?: any;
          field: string;
          operator: string;
          value: any;
          type?: string;
          time_window?: string;
        }[];
      }[];
    };
    metrics: any[];
    status: "paused" | "active";
    automation_actions: {
      actions: Array<{
        type: "send_to_inbox" | "send_to_slack" | "send_to_email";
        playbook_id?: string;
        assign_to: string;
        assign_to_key: string;
        channel_id?: string;
        slack_user_id?: string;
        message_type?: "channel" | "direct";
        message?: string;
        to?: string;
        subject?: string;
        body?: string;
      }>;
      suppression_period: "all_time" | "daily",
      playbook_id?: string;
    };
    time_window: string;
    selected_fields: string[];
    time_zone: string;
    owner_id: string;
    workspace_id: string;
    responseData: any;
  };
  setSignal: (entity: Store["signal"]) => void;
  setName: (name: string) => void;
  setTrigger: (trigger: Store["signal"]["trigger"]) => void;
  setEntityRelation: (entity_relation: Store["signal"]["entity_relation"]) => void;
  setAutomationType: (automation_type: Store["signal"]["automation_type"]) => void;
  addFilter: (filter: Store["signal"]["filters"]["predicates"][0]) => void;
  editFilter: (filter: Store["signal"]["filters"]["predicates"][0], index: number) => void;
  deleteFilter: (index: number) => void;
  clearFilter: () => void;
  clearSignal: () => void;
  setMetrics: (metrics: Store["signal"]["metrics"]) => void;
  setStatus: (status: Store["signal"]["status"]) => void;
  setTimeWindow: (time_window: Store["signal"]["time_window"]) => void;
  setSelectedFields: (selected_fields: Store["signal"]["selected_fields"]) => void;
  setTimeZone: (time_zone: Store["signal"]["time_zone"]) => void;
  editPredicate: (predicate: Store["signal"]["filters"]["predicates"][0]["predicates"][0], index: number, filterIndex: number) => void;
  addPredicate: (predicate: Store["signal"]["filters"]["predicates"][0]["predicates"][0], filterIndex: number) => void;
  deletePredicate: (index: number, filterIndex: number) => void;
  setSuppressionPeriod: (suppression_period: Store["signal"]["automation_actions"]["suppression_period"]) => void;
  createAction: (action: Store["signal"]["automation_actions"]["actions"][0]) => void;
  deleteAction: (type: Store["signal"]["automation_actions"]["actions"][0]["type"]) => void;
  updateAction: (action: Store["signal"]["automation_actions"]["actions"][0], type: Store["signal"]["automation_actions"]["actions"][0]["type"]) => void;
  setSignalActiveUsers: () => void;
  setSignalPaidUsers: () => void;
  setSignalHighGrowthAccounts: () => void;
  view: any;
  setView: (view: any) => void;
  updateView: (view: any) => void;
  clearViewFilter: () => void;
  editViewPredicate: (predicate: any, index: number) => void;
  deleteViewPredicate: (index: number) => void;
  addViewPredicate: (predicate: any) => void;
  setViewSelectedFields: (fields: any) => void;
  setViewTimeWindow: (time_window: string) => void;
  setViewMetrics: (metrics: any) => void;
  setPlaybook: (playbook_id: any) => void;
  setResponseData: (responseData: any) => void;
};

const useGettingStartedStore = create<Store>((set) => ({
  signal: {
    name: "",
    description: "",
    trigger: "usage_based",
    entity_relation: "accounts",
    automation_type: "signal",
    is_precreated: false,
    filters: {
      query_type: "OR",
      predicates: [{
        query_type: "AND",
        predicates: [{
          field: "",
          operator: "",
          value: "",
        }],
      }],
    },
    metrics: [],
    status: "paused",
    automation_actions: {
      actions: [],
      suppression_period: "all_time",
    },
    time_window: "last_30_days",
    selected_fields: [],
    time_zone: "",
    owner_id: "",
    workspace_id: "",
    responseData: {},
  },
  setResponseData: (responseData) => set(
    (state) => ({ signal: { ...state.signal, responseData } }),
  ),
  setSignal: (signal) => set({ signal }),
  setName: (name) => set((state) => ({ signal: { ...state.signal, name } })),
  setTrigger: (trigger) => set((state) => ({ signal: { ...state.signal, trigger } })),
  setPlaybook: (playbook_id: any) => set((state) => ({
    signal: {
      ...state.signal,
      automation_actions: {
        ...state.signal.automation_actions,
        playbook_id,
      },
    },
  })),
  setEntityRelation: (entity_relation) => set(
    (state) => ({ signal: { ...state.signal, entity_relation } }),
  ),
  setAutomationType: (automation_type) => set(
    (state) => ({ signal: { ...state.signal, automation_type } }),
  ),
  addFilter: (filter) => set(
    (state) => ({
      signal: {
        ...state.signal,
        filters: {
          ...state.signal.filters,
          predicates: [...state.signal.filters.predicates, filter],
        },
      },
    }),
  ),
  editFilter: (filter, index) => set((state) => {
    const filters = [...state.signal.filters.predicates];
    filters[index] = filter;
    return {
      signal: {
        ...state.signal,
        filters: {
          ...state.signal.filters,
          predicates: filters,
        },
      },
    };
  }),
  deleteFilter: (index) => set((state) => {
    const filters = [...state.signal.filters.predicates];
    filters.splice(index, 1);
    return {
      signal: {
        ...state.signal,
        filters: {
          ...state.signal.filters,
          predicates: filters,
        },
      },
    };
  }),
  clearFilter: () => set((state) => ({
    signal: {
      ...state.signal,
      filters: {
        query_type: "OR",
        predicates: [{
          query_type: "AND",
          predicates: [{
            field: "",
            operator: "",
            value: "",
          }],
        }],
      },
    },
  })),
  editPredicate: (predicate, index, filterIndex) => set((state) => {
    const filters = [...state.signal.filters.predicates];
    const predicates = [...state.signal.filters.predicates[filterIndex].predicates];
    predicates[index] = predicate;
    filters[filterIndex].predicates = predicates;
    return {
      signal: {
        ...state.signal,
        filters: {
          ...state.signal.filters,
          predicates: filters,
        },
      },
    };
  }),
  addPredicate: (predicate, filterIndex) => set((state) => {
    const filters = [...state.signal.filters.predicates];
    filters[filterIndex].predicates.push(predicate);
    return {
      signal: {
        ...state.signal,
        filters: {
          ...state.signal.filters,
          predicates: filters,
        },
      },
    };
  }),
  deletePredicate: (index, filterIndex) => set((state) => {
    const filters = [...state.signal.filters.predicates];
    filters[filterIndex].predicates.splice(index, 1);
    return {
      signal: {
        ...state.signal,
        filters: {
          ...state.signal.filters,
          predicates: filters,
        },
      },
    };
  }),
  clearSignal: () => set(() => ({
    signal: {
      name: "",
      description: "",
      trigger: "usage_based",
      entity_relation: "accounts",
      automation_type: "signal",
      is_precreated: false,
      filters: {
        query_type: "OR",
        predicates: [{
          query_type: "AND",
          predicates: [{
            field: "",
            operator: "",
            value: "",
          }],
        }],
      },
      metrics: [],
      status: "paused",
      automation_actions: {
        actions: [],
        suppression_period: "all_time",
      },
      time_window: "last_30_days",
      selected_fields: [],
      time_zone: "",
      owner_id: "",
      workspace_id: "",
    },
  })),
  setSelectedFields: (selected_fields) => set((state) => ({
    signal: {
      ...state.signal,
      selected_fields,
    },
  })),
  setMetrics: (metrics) => set((state) => ({
    signal: {
      ...state.signal,
      metrics,
    },
  })),
  setTimeWindow: (time_window) => set((state) => ({
    signal: {
      ...state.signal,
      time_window,
    },
  })),
  setStatus: (status) => set((state) => ({
    signal: {
      ...state.signal,
      status,
    },
  })),
  setTimeZone: (time_zone) => set((state) => ({
    signal: {
      ...state.signal,
      time_zone,
    },
  })),
  setSuppressionPeriod: (suppression_period) => set((state) => ({
    signal: {
      ...state.signal,
      automation_actions: {
        ...state.signal.automation_actions,
        suppression_period,
      },
    },
  })),
  createAction: (action) => set((state) => {
    const actions = [...state.signal.automation_actions.actions];
    actions.push(action);
    return {
      signal: {
        ...state.signal,
        automation_actions: {
          ...state.signal.automation_actions,
          actions,
        },
      },
    };
  }),
  deleteAction: (type) => set((state) => {
    const actions = [...state.signal.automation_actions.actions]
      .filter((action) => action.type !== type);
    return {
      signal: {
        ...state.signal,
        automation_actions: {
          ...state.signal.automation_actions,
          actions,
        },
      },
    };
  }),
  updateAction: (action, type) => set((state) => {
    let actions;
    if (!state?.signal?.automation_actions?.actions?.find((act) => act?.type === type)) {
      actions = [...state.signal.automation_actions.actions];
      actions.push(action);
    } else {
      actions = [...state.signal.automation_actions.actions].map((act: any) => {
        if (act.type === type) {
          const updatedAct = {
            ...act,
            ...action,
          };
          return updatedAct;
        }
        return act;
      });
    }
    return {
      signal: {
        ...state.signal,
        automation_actions: {
          ...state.signal.automation_actions,
          actions,
        },
      },
    };
  }),
  setSignalActiveUsers: () => set(() => ({
    signal: {
      name: "Active Users",
      description: "Users who have logged in at least once in the last 30 days",
      trigger: "usage_based",
      entity_relation: "users",
      automation_type: "signal",
      is_precreated: false,
      filters: {
        query_type: "OR",
        predicates: [{
          query_type: "AND",
          predicates: [
            {
              field: "users.last_seen",
              operator: "before",
              value: "Last 30 days",
              type: "timestamp",
              entity_type: "column",
            },
          ],
        }],
      },
      metrics: [],
      status: "active",
      automation_actions: {
        actions: [{
          type: "send_to_inbox",
          assign_to: "csm",
          assign_to_key: `csm_`,
        }],
        suppression_period: "all_time",
      },
      time_window: "last_30_days",
      selected_fields: ["users.last_seen"],
      time_zone: "",
      owner_id: "",
      workspace_id: "",
    },
  })),
  setSignalPaidUsers: () => set(() => ({
    signal: {
      name: "Paid Accounts",
      description: "Accounts who have paid at least once",
      trigger: "usage_based",
      entity_relation: "accounts",
      automation_type: "signal",
      is_precreated: false,
      filters: {
        query_type: "OR",
        predicates: [{
          query_type: "AND",
          predicates: [
            {
              field: "accounts.subscription_status",
              operator: "starts_with",
              value: "subscribed",
              type: "string",
              entity_type: "column",
            },
          ],
        }],
      },
      metrics: [],
      status: "active",
      automation_actions: {
        actions: [{
          type: "send_to_inbox",
          assign_to: "csm",
          assign_to_key: `csm_`,
        }],
        suppression_period: "all_time",
      },
      time_window: "last_30_days",
      selected_fields: ["accounts.subscription_status"],
      time_zone: "",
      owner_id: "",
      workspace_id: "",
    },
  })),
  setSignalHighGrowthAccounts: () => set(() => ({
    signal: {
      name: "High Growth Accounts",
      description: "Accounts with more than 10% growth in the last 30 days",
      trigger: "usage_based",
      entity_relation: "accounts",
      automation_type: "signal",
      is_precreated: false,
      filters: {
        query_type: "OR",
        predicates: [{
          query_type: "AND",
          predicates: [
            {
              field: "default_metric_total_actions",
              operator: "gt",
              value: 1,
              type: "int",
              entity_type: "changeval",
              time_window: "last_30_days",
            },
          ],
        }],
      },
      metrics: ["default_metric_total_actions"],
      status: "active",
      automation_actions: {
        actions: [{
          type: "send_to_inbox",
          assign_to: "csm",
          assign_to_key: `csm_`,
        }],
        suppression_period: "all_time",
      },
      time_window: "last_30_days",
      selected_fields: ["accounts.last_seen"],
      time_zone: "",
      owner_id: "",
      workspace_id: "",
    },
  })),

  view: {
    view_name: "",
    entity_relation: "accounts",
    view_icon: null,
    owner_id: null,
    metrics_time_frame: "last_30_days",
    sort_fields: {
      type: "property",
      field: "accounts.last_seen",
      order: "desc",
    },
    metrics: [
      "default_metric_total_active_users",
    ],
    updated_at: "2023-08-01T07:17:19.443543",
    is_precreated: true,
    view_description: "All accounts under management",
    id: "a63ab720-1155-4ff9-aaf6-bc3e4f569454",
    filters: {
      query_type: "AND",
      predicates: [{
        field: "",
        operator: "",
        value: "",
      }],
    },
    is_public: true,
    created_at: "2023-08-01T07:17:19.443480",
    selected_columns: [
      "accounts.name",
      "accounts.external_id",
      "accounts.avatar_url",
      "accounts.record_created_at",
      "accounts.updated_at",
      "accounts.subscription_status",
      "accounts.last_seen",
    ],
    page_limit: 10,
    is_pinned: false,
  },
  setView: (view: any) => set({ view }),
  updateView: (view: any) => set(
    (state: any) => (
      { view: { ...state.view, ...view } }),
  ),
  clearViewFilter: () => set((state) => ({
    view: {
      ...state.view,
      filters: {
        query_type: "OR",
        predicates: [{
          query_type: "AND",
          predicates: [{
            field: "",
            operator: "",
            value: "",
          }],
        }],
      },
    },
  })),
  editViewPredicate: (predicate, index) => set((state) => {
    const predicates = [...state.view.filters.predicates];
    predicates[index] = predicate;
    return {
      view: {
        ...state.view,
        filters: {
          ...state.view.filters,
          predicates,
        },
      },
    };
  }),
  addViewPredicate: (predicate) => set((state) => {
    const { filters } = state.view;
    if (Object.keys(filters).length === 0) {
      return {
        view: {
          ...state.view,
          filters: {
            query_type: "AND",
            predicates: [predicate],
          },
        },
      };
    } if (filters?.predicates?.length === 0 || filters?.predicates[filters.predicates.length - 1].field !== "") filters.predicates.push(predicate);
    return {
      view: {
        ...state.view,
        filters: {
          ...state.view.filters,
          predicates: filters.predicates,
        },
      },
    };
  }),
  deleteViewPredicate: (index) => set((state) => {
    const { filters } = state.view;
    filters.predicates.splice(index, 1);
    return {
      view: {
        ...state.view,
        filters: {
          ...state.view.filters,
          predicates: filters.predicates,
        },
      },
    };
  }),
  setViewSelectedFields: (selected_fields) => set((state) => ({
    view: {
      ...state.view,
      selected_fields,
    },
  })),
  setViewMetrics: (metrics) => set((state) => ({
    view: {
      ...state.view,
      metrics,
    },
  })),
  setViewTimeWindow: (time_window) => set((state) => ({
    view: {
      ...state.view,
      metrics_time_frame: time_window,
    },
  })),
}));

export default useGettingStartedStore;
